// 내 현황

import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { MoreHorizontal, Search } from 'lucide-react'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import type { ColDef } from 'ag-grid-community'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { fetchBusinesses } from '@/store/businessSlice'
import { fetchTaskSummary } from '@/store/taskSummarySlice'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import TaskItemCreatePanel from '@/pages/project/TaskManagement/components/TaskItemCreatePanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import TaskItemsTable from '../DetailDashboard/components/TaskItemsTable'
import TaskBudgetsTable from '../DetailDashboard/components/TaskBudgetsTable'
import TaskPerformancesTable from '../DetailDashboard/components/TaskPerformancesTable'
import TasksTable from '../DetailDashboard/components/TasksTable'
import TaskItemsListPanel from '../DetailDashboard/components/TaskItemsListPanel'
import TasksListPanel from '../DetailDashboard/components/TasksListPanel'
import TaskBudgetsListPanel from '../DetailDashboard/components/TaskBudgetsListPanel'
import TaskPerformancesListPanel from '../DetailDashboard/components/TaskPerformancesListPanel'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import DateRangePicker from '@/components/ui/DateRangePicker'
import { getInitialDateRange, formatDateForAPI } from '@/components/ui/DateRangePicker/utils'
import Chart from '@/components/data/Chart'
import { TaskSummaryMonthlyResponse, TaskSummaryService, TaskSummaryTotalResponse, TaskReportService } from '@/api'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'
import { useShowTasks } from '@/hooks/useShowTasks'

const MyTaskDashboardPage: React.FC = () => {
  // Redux selectors and dispatch
  const dispatch = useAppDispatch()
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 요약 행·탭을 숨긴다
  const showTasks = useShowTasks()
  const currentUser = useAppSelector(state => state.auth.orgMember)
  const { businesses, isLoading: businessLoading, error: businessError } = useAppSelector(state => state.business)
  const { summary: taskSummary, isLoading: isSummaryLoading, error: summaryError } = useAppSelector(state => state.taskSummary)

  // Selector state for filtering (removed member selection - always current user)
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('')
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string>('')
  const {
    businessId: selectedBusinessId,
    businessName: selectedBusinessName,
    setBusiness: saveSelectedBusiness,
  } = usePersistedBusinessFilter()

  // Period view state
  const [periodViewType, setPeriodViewType] = useState<'period' | 'all'>('period')
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<'period'>('period')

  // Date range picker state - initialize from localStorage
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const [initialStart] = getInitialDateRange()
    return initialStart
  })
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const [, initialEnd] = getInitialDateRange()
    return initialEnd
  })

  // List view state
  const [selectedListType, setSelectedListType] = useState< 'work' | 'task' | 'budget' | 'performance'>('work')

  // Search state
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchTrigger, setSearchTrigger] = useState(0)  // Explicit search trigger

  // Task item status filter state
  const [taskItemStatusFilter, setTaskItemStatusFilter] = useState<string>('')

  // Task (세부실행과제) status filter state
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('')

  // 예산/성과 상태 필터 — 백엔드에서 2-state(incomplete/completed) 지원
  const [budgetStatusFilter, setBudgetStatusFilter] = useState<string>('')
  const [performanceStatusFilter, setPerformanceStatusFilter] = useState<string>('')

  // ===== 상세 패널 — id만 보관, 데이터는 TaskDetailPanel이 자체 fetch =====
  const [taskDetailRefreshKey, setTaskDetailRefreshKey] = useState(0)

  // Task detail panel state (type='task')
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  const [selectedTaskIdForDetail, setSelectedTaskIdForDetail] = useState<string | null>(null)

  // Task item detail panel state (type='task_item')
  const [isTaskItemDetailPanelOpen, setIsTaskItemDetailPanelOpen] = useState(false)
  const [selectedTaskItemId, setSelectedTaskItemId] = useState<string | null>(null)

  // Budget item detail panel state (type='task')
  const [isBudgetDetailPanelOpen, setIsBudgetDetailPanelOpen] = useState(false)
  const [selectedBudgetTaskId, setSelectedBudgetTaskId] = useState<string | null>(null)

  // Performance item detail panel state (type='task')
  const [isPerformanceDetailPanelOpen, setIsPerformanceDetailPanelOpen] = useState(false)
  const [selectedPerformanceTaskId, setSelectedPerformanceTaskId] = useState<string | null>(null)

  // 요약 표 셀 / 월별 차트 봉 클릭 시 열리는 리스트 사이드패널 상태.
  // 한 번에 하나만 열리므로 단일 state로 관리.
  type SummaryListPanelType = 'task_item' | 'task' | 'budget' | 'performance'
  type SummaryListPanelStatus = 'all' | 'completed' | 'incomplete'
  const [summaryListPanel, setSummaryListPanel] = useState<{
    type: SummaryListPanelType
    statusBucket: SummaryListPanelStatus
    title: string
    /** 차트의 특정 월(봉/포인트)을 클릭했을 때만 채워짐. 페이지 기간 필터를 덮어씀. */
    dateRangeOverride?: { start: Date; end: Date }
  } | null>(null)

  // Report detail panel state
  // Report detail panel state — 패널이 자체 fetch하므로 task + selectedTemplate만 보관
  const [reportPanelState, setReportPanelState] = useState({
    isOpen: false,
    selectedTask: null as TaskTreeNode | null,
    selectedTemplate: null as { key: string; name: string } | null,
  })

  // Action button click flag to prevent row click when action button is clicked
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)

  // Report templates state
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  // Task edit panel state
  const [isTaskEditPanelOpen, setIsTaskEditPanelOpen] = useState(false)
  const [editTaskData, setEditTaskData] = useState<{
    id: string
    name: string
    taskSubgroupId: string
    taskSubgroupName: string
    taskGroupName: string
    businessName: string
    businessId: string
    assigneeId: string
    managerId: string
    reviewerId: string
    startAt: string
    endAt: string
    description: string
    /** 편집 패널의 budget/performance 초기값에 사용 (raw originalData) */
    taskOriginal?: any
  } | null>(null)

  // Task item edit panel state
  const [isTaskItemEditPanelOpen, setIsTaskItemEditPanelOpen] = useState(false)
  const [editTaskItemData, setEditTaskItemData] = useState<{
    id: string
    name: string
    taskId: string
    taskName: string
    taskSubgroupName: string
    businessName: string
    output: string
    startAt: string
    endAt: string
  } | null>(null)
  // isRefreshingTask는 handleTasksDataRefreshed에서 "수정 후 새로고침 중" 여부 판단에 사용
  const [isRefreshingTask, setIsRefreshingTask] = useState(false)
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(0)
  // 수행업무/예산/성과 목록 새로고침 트리거 — 각 테이블이 자체 상태로 조회하므로
  // 부모는 값만 올려 재조회를 요청한다 (기존에는 부모가 전역 스토어를 직접 갱신했다)
  const [taskItemRefreshTrigger, setTaskItemRefreshTrigger] = useState(0)
  const [taskBudgetRefreshTrigger, setTaskBudgetRefreshTrigger] = useState(0)
  const [taskPerformanceRefreshTrigger, setTaskPerformanceRefreshTrigger] = useState(0)
  const [editSourcePanel, setEditSourcePanel] = useState<'task' | 'budget' | 'performance' | null>(null)

  // Monthly summary state for 전체 tab
  const [monthlySummary, setMonthlySummary] = useState<TaskSummaryMonthlyResponse | null>(null)
  const [isMonthlySummaryLoading, setIsMonthlySummaryLoading] = useState(false)
  const [monthlySummaryError, setMonthlySummaryError] = useState<string | null>(null)

  // Summary data interface
  interface SummaryDataItem {
    id: string
    itemName:  '수행업무' | '세부실행과제' | '예산' | '성과'
    total: number
    completed: number
    incomplete: number
    ratio: number
    unit?: string
  }

  // API response to UI data mapping function for regular summary
  const mapApiToSummaryData = useCallback((apiResponse: TaskSummaryTotalResponse): SummaryDataItem[] => [
    {
      id: '1',
      itemName: '수행업무',
      total: apiResponse.task_item.total.total_count,
      completed: apiResponse.task_item.total.completed_count,
      incomplete: apiResponse.task_item.total.incomplete_count,
      ratio: apiResponse.task_item.total.count_percentage
    },
    {
      id: '2',
      itemName: '세부실행과제',
      total: apiResponse.task.total.total_count,
      completed: apiResponse.task.total.completed_count,
      incomplete: apiResponse.task.total.incomplete_count,
      ratio: apiResponse.task.total.count_percentage
    },
    {
      id: '3',
      itemName: '예산',
      total: apiResponse.task_budget.total.total_target_figure,
      completed: apiResponse.task_budget.total.total_done_figure,
      incomplete: apiResponse.task_budget.total.total_left_figure,
      ratio: apiResponse.task_budget.total.figure_percentage,
      unit: '원'
    },
    {
      id: '4',
      itemName: '성과',
      total: apiResponse.task_performance.total.total_count,
      completed: apiResponse.task_performance.total.completed_count,
      incomplete: apiResponse.task_performance.total.incomplete_count,
      ratio: apiResponse.task_performance.total.count_percentage
    }
  ], [])

  // Monthly API response to UI data mapping function for 전체 tab
  const mapMonthlyApiToSummaryData = useCallback((apiResponse: TaskSummaryMonthlyResponse): SummaryDataItem[] => [
    {
      id: '1',
      itemName: '수행업무',
      total: apiResponse.task_item.total.total_count,
      completed: apiResponse.task_item.total.completed_count,
      incomplete: apiResponse.task_item.total.incomplete_count,
      ratio: apiResponse.task_item.total.count_percentage
    },
    {
      id: '2',
      itemName: '세부실행과제',
      total: apiResponse.task.total.total_count,
      completed: apiResponse.task.total.completed_count,
      incomplete: apiResponse.task.total.incomplete_count,
      ratio: apiResponse.task.total.count_percentage
    },
    {
      id: '3',
      itemName: '예산',
      total: apiResponse.task_budget.total.total_target_figure,
      completed: apiResponse.task_budget.total.total_done_figure,
      incomplete: apiResponse.task_budget.total.total_left_figure,
      ratio: apiResponse.task_budget.total.figure_percentage,
      unit: '원'
    },
    {
      id: '4',
      itemName: '성과',
      total: apiResponse.task_performance.total.total_count,
      completed: apiResponse.task_performance.total.completed_count,
      incomplete: apiResponse.task_performance.total.incomplete_count,
      ratio: apiResponse.task_performance.total.count_percentage
    }
  ], [])

  // Chart data transformation for monthly task data
  const transformMonthlyDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '완료', type: 'column', data: [] },
          { name: '완료율', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const incompleteData = sortedMonths.map(month => {
      const value = monthlyData[month]?.incomplete_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data
    const totalData = sortedMonths.map((month, index) => {
      return completedData[index] + incompleteData[index]
    })

    return {
      categories,
      monthKeys: sortedMonths, // YYYY-MM (categories와 동일 순서). 차트 봉/포인트 클릭 시 월 인덱스 → 월 키 매핑용
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '완료',
          type: 'column',
          data: completedData
        },
        {
          name: '완료율',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task item data
  const transformMonthlyTaskItemDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '완료', type: 'column', data: [] },
          { name: '완료율', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const incompleteData = sortedMonths.map(month => {
      const value = monthlyData[month]?.incomplete_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data
    const totalData = sortedMonths.map((month, index) => {
      return completedData[index] + incompleteData[index]
    })

    return {
      categories,
      monthKeys: sortedMonths, // YYYY-MM (categories와 동일 순서). 차트 봉/포인트 클릭 시 월 인덱스 → 월 키 매핑용
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '완료',
          type: 'column',
          data: completedData
        },
        {
          name: '완료율',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task budget data
  const transformMonthlyTaskBudgetDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '집행', type: 'column', data: [] },
          { name: '집행률', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_done_figure
      return typeof value === 'number' ? value : 0
    })
    const remainingData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_left_figure
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.figure_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data
    const totalData = sortedMonths.map((month, index) => {
      return completedData[index] + remainingData[index]
    })

    return {
      categories,
      monthKeys: sortedMonths, // YYYY-MM (categories와 동일 순서). 차트 봉/포인트 클릭 시 월 인덱스 → 월 키 매핑용
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '집행',
          type: 'column',
          data: completedData
        },
        {
          name: '집행률',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task performance data
  const transformMonthlyTaskPerformanceDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '달성', type: 'column', data: [] },
          { name: '달성률', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const incompleteData = sortedMonths.map(month => {
      const value = monthlyData[month]?.incomplete_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    // Calculate total data
    const totalData = sortedMonths.map((month, index) => {
      return completedData[index] + incompleteData[index]
    })

    return {
      categories,
      monthKeys: sortedMonths, // YYYY-MM (categories와 동일 순서). 차트 봉/포인트 클릭 시 월 인덱스 → 월 키 매핑용
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '달성',
          type: 'column',
          data: completedData
        },
        {
          name: '달성률',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart options for stacked bar + line chart - OverallDashboard pattern
  const getChartOptions = useCallback((
    categories: string[],
    chartType: 'task' | 'work' | 'budget' | 'performance' = 'task',
    seriesData?: any[],
    /**
     * 차트 영역 클릭 시 호출될 핸들러를 담은 ref.
     * ApexCharts가 이벤트 핸들러를 매 렌더마다 재바인딩하지 않을 수 있어 ref를 통해 항상 최신 핸들러를 호출한다.
     * 인자는 ApexCharts가 전달한 dataPointIndex (데이터 포인트가 아닌 영역 클릭 시 -1).
     */
    handlerRef?: React.MutableRefObject<((dataPointIndex: number) => void) | null>,
  ) => {
    // Get chart type specific configurations
    const getChartConfig = () => {
      switch (chartType) {
        case 'task':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '세부실행과제 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
        case 'work':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '수행업무 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
        case 'budget':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '집행',
            thirdSeriesName: '집행률',
            yAxisTitle: '예산 금액',
            percentageTitle: '집행률 (%)',
            unit: '원'
          }
        case 'performance':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '달성',
            thirdSeriesName: '달성률',
            yAxisTitle: '성과 과제 수',
            percentageTitle: '달성률 (%)',
            unit: '개'
          }
        default:
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '세부실행과제 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
      }
    }

    const config = getChartConfig()

    // Calculate max value from both column series to sync Y-axis scales
    let sharedMax: number | undefined
    if (seriesData && seriesData.length >= 2) {
      const series0Data = seriesData[0]?.data || []
      const series1Data = seriesData[1]?.data || []
      const max0 = Math.max(...series0Data, 0)
      const max1 = Math.max(...series1Data, 0)
      const rawMax = Math.max(max0, max1)
      // Round up to nearest 10 (e.g., 93 -> 100, 141 -> 150)
      sharedMax = Math.ceil(rawMax / 100) * 100
    }

    return {
      chart: {
        type: 'line' as const,
        stacked: false,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        events: {
          // 차트 SVG 영역 어디든 클릭 시 발화. config.dataPointIndex가 -1(데이터 포인트가 아닌 영역)이면
          // 핸들러가 아무 목록도 열지 않는다. x축 월 라벨은 아래 xAxisLabelClick이 처리한다.
          // ref를 통해 항상 최신 핸들러를 호출 (ApexCharts의 stale closure 캐싱 이슈 우회).
          click: (event: any, _chartContext: any, config: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof config?.dataPointIndex === 'number' ? config.dataPointIndex : -1
            handlerRef?.current?.(idx)
          },
          // 봉(column)/마커(line) 위 정확한 클릭에서 추가로 발화. click과 함께 발화하더라도
          // 동일 핸들러를 부르므로 setState가 두 번 들어가는 정도로, 결과는 같다.
          dataPointSelection: (event: any, _chartContext: any, config: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof config?.dataPointIndex === 'number' ? config.dataPointIndex : -1
            if (idx >= 0) handlerRef?.current?.(idx)
          },
          // x축 월 라벨 클릭 → 봉/포인트 클릭과 동일하게 해당 월로 좁힌 목록을 연다.
          // labelIndex는 categories 인덱스이고 monthKeys와 1:1로 대응한다.
          xAxisLabelClick: (event: any, _chartContext: any, opts: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof opts?.labelIndex === 'number' ? opts.labelIndex : -1
            if (idx >= 0) handlerRef?.current?.(idx)
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
        },
      },
      stroke: {
        width: [0, 0, 3],
        curve: 'smooth' as const,
      },
      xaxis: {
        categories: categories,
        title: {
          text: '월별',
          style: {
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            color: designTokens.colors.gray[5],
          },
        },
        labels: {
          style: {
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            colors: designTokens.colors.gray[4],
            // 월 라벨도 클릭하면 해당 월 목록이 열리므로 클릭 가능 커서를 표시한다 (globals.css)
            cssClass: 'dot-clickable-xaxis-label',
          },
        },
      },
      yaxis: [
        {
          seriesName: config.firstSeriesName,
          title: {
            text: config.yAxisTitle,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
            },
          },
          labels: {
            formatter: (value: number) => {
              if (chartType === 'budget') {
                return `${Math.round(value).toLocaleString()}원`
              }
              return `${value.toLocaleString()}${config.unit}`
            },
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              colors: designTokens.colors.gray[4],
            },
          },
          ...(sharedMax !== undefined && { max: sharedMax, min: 0, stepSize:10, tickAmount:5, forceNiceScale: true }),
        },
        {
          seriesName: config.secondSeriesName,
          show: false,
          ...(sharedMax !== undefined && { max: sharedMax, min: 0, stepSize:10, tickAmount:5, forceNiceScale: true }),
        },
        {
          seriesName: config.thirdSeriesName,
          opposite: true,
          title: {
            text: config.percentageTitle,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
            },
          },
          labels: {
            formatter: (value: number) => `${value.toFixed(1)}%`,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              colors: designTokens.colors.gray[4],
            },
          },
          ...({ max: 100, min: 0, stepSize:10, tickAmount:10, forceNiceScale: true }),
        },
      ],
      colors: [designTokens.colors.gray[3], designTokens.colors.primary.dark, designTokens.colors.warning.dark],
      legend: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '12px',
        position: 'top' as const,
        horizontalAlign: 'center' as const,
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '12px',
        },
        y: {
          formatter: (value: number, { seriesIndex }: any) => {
            if (seriesIndex === 2) {
              return `${value.toFixed(1)}%`
            }
            if (chartType === 'budget') {
              return `${Math.round(value).toLocaleString()}원`
            }
            return `${value.toLocaleString()}${config.unit}`
          },
        },
      },
      grid: {
        borderColor: designTokens.colors.gray[2],
      },
    }
  }, [])

  // Summary data preparation with API integration
  const summaryData = useMemo((): SummaryDataItem[] => {
    let rows: SummaryDataItem[]
    if (periodViewType === 'all') {
      // For 전체 tab, use monthly summary if available
      rows = isMonthlySummaryLoading || !monthlySummary ? [] : mapMonthlyApiToSummaryData(monthlySummary)
    } else {
      // For 기간별 tab, use regular summary
      rows = isSummaryLoading || !taskSummary ? [] : mapApiToSummaryData(taskSummary)
    }
    return showTasks ? rows : rows.filter(row => row.itemName !== '세부실행과제')
  }, [periodViewType, taskSummary, isSummaryLoading, monthlySummary, isMonthlySummaryLoading, mapApiToSummaryData, mapMonthlyApiToSummaryData, showTasks])

  // 세부실행과제 표시가 꺼지면 선택돼 있던 세부실행과제 탭에서 수행업무 탭으로 옮긴다
  useEffect(() => {
    if (!showTasks && selectedListType === 'task') {
      setSelectedListType('work')
    }
  }, [showTasks, selectedListType])

  // Load businesses when workspace changes
  useEffect(() => {
    if (selectedWorkspace?.id) {
      dispatch(fetchBusinesses({
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 100
      }))
    }
  }, [selectedWorkspace?.id, dispatch])

  // Fetch monthly summary for 전체 tab (filtered by current user)
  const fetchMonthlySummary = useCallback(async () => {
    if (!selectedWorkspace?.id || !currentUser?.id) {
      setMonthlySummaryError('워크스페이스 또는 사용자 정보가 없습니다.')
      return
    }

    setIsMonthlySummaryLoading(true)
    setMonthlySummaryError(null)

    try {
      const filters = {
        workspaceId: selectedWorkspace.id,
        stakeholderId: currentUser.id, // Always filter by current user (org member id)
        departmentId: selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : undefined,
        businessId: selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : undefined,
        startDate: periodViewType === 'period' && startDate ? formatDateForAPI(startDate) : undefined,
        endDate: periodViewType === 'period' && endDate ? formatDateForAPI(endDate) : undefined
      }

      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryMonthly(filters)
      setMonthlySummary(response)
    } catch (err) {
      console.error('Failed to fetch monthly summary:', err)
      setMonthlySummaryError('월별 요약 정보를 불러오는데 실패했습니다.')
    } finally {
      setIsMonthlySummaryLoading(false)
    }
  }, [
    selectedWorkspace?.id,
    currentUser?.id,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    periodViewType,
    startDate,
    endDate
  ])

  // Fetch task summary when workspace or filters change (기간별 tab only, filtered by current user)
  useEffect(() => {
    // Only call regular summary API for 기간별 tab (not 전체 tab)
    if (periodViewType === 'all' || !selectedWorkspace?.id || !currentUser?.id) {
      return
    }

    const filters = {
      workspaceId: selectedWorkspace.id,
      stakeholderId: currentUser.id, // Always filter by current user (org member id)
      departmentId: selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : null,
      businessId: selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : null,
      startDate: periodViewType === 'period' && startDate ? formatDateForAPI(startDate) : null,
      endDate: periodViewType === 'period' && endDate ? formatDateForAPI(endDate) : null
    }

    dispatch(fetchTaskSummary(filters))
  }, [
    selectedWorkspace?.id,
    currentUser?.id,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    startDate,
    endDate,
    periodViewType,
    dispatch
  ])

  // Fetch monthly summary when in 전체 tab
  useEffect(() => {
    if (periodViewType === 'all' && selectedWorkspace?.id && currentUser?.id) {
      fetchMonthlySummary()
    }
  }, [
    periodViewType,
    selectedWorkspace?.id,
    currentUser?.id,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    startDate,
    endDate,
    fetchMonthlySummary
  ])

  // Selector handlers (simplified - no member selection)
  const handleDepartmentSelect = (departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)
  }

  const handleBusinessSelect = (businessId: string, businessName: string) => {
    saveSelectedBusiness(businessId, businessName)
  }

  // Clear all filters (simplified - only department and business)
  const clearAllFilters = () => {
    setSelectedDepartmentId('')
    setSelectedDepartmentName('')
    saveSelectedBusiness('', '')
  }

  // const handleMoreOptions = () => {
  //   console.warn('더보기 기능 미구현')
  // }

  const handlePeriodViewChange = (viewType: 'period' | 'all') => {
    setPeriodViewType(viewType)
  }

  const handleTimePeriodChange = (period: 'daily' | 'weekly' | 'monthly') => {
    setSelectedTimePeriod(period)
    // Clear current date selection when period changes
    setStartDate(null)
    setEndDate(null)
  }

  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput.trim())
    setSearchTrigger(prev => prev + 1)  // Trigger search execution
  }, [searchInput])

  const handleListTypeChange = (listType: 'task' | 'work' | 'budget' | 'performance') => {
    setSelectedListType(listType)
    // Clear search when switching tabs
    setSearchInput('')
    setSearchQuery('')
  }

  // (convertToTaskTreeNode 제거 — TaskDetailPanel이 자체 fetch + 변환)

  // Handle task row click (matches TasksTable onTaskClick signature)
  // 패널이 자체 fetch하므로 id만 보관
  const handleTaskRowClick = (task: TaskTreeNode) => {
    setSelectedTaskIdForDetail(task.id)
    setIsDetailPanelOpen(true)
  }

  // Handle task detail panel close
  const handleDetailPanelClose = () => {
    setIsDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)
  }

  // Handle task detail panel actions
  const handleTaskEdit = (task: TaskTreeNode) => {
    if (task.type === 'task' && task.originalData) {
      const taskData = task.originalData as any
      setEditTaskData({
        id: task.id,
        name: task.name,
        taskSubgroupId: taskData.task_subgroup_id || '',
        taskSubgroupName: taskData.task_subgroup_name || '',
        taskGroupName: taskData.task_group_name || '',
        businessName: taskData.business_name || '',
        businessId: taskData.business_id || '',
        assigneeId: task.assignee?.id || '',
        managerId: task.manager?.id || '',
        reviewerId: task.reviewer?.id || '',
        startAt: taskData.start_at || '',
        endAt: taskData.end_at || '',
        description: taskData.description || '',
        // budget/performance 초기값을 편집 패널에 넘기기 위해 보관
        taskOriginal: taskData
      })
      setSelectedTaskIdForDetail(task.id)  // 편집 패널 렌더 조건용

      // Track which panel opened the edit BEFORE any panel state changes
      if (isBudgetDetailPanelOpen) {
        setEditSourcePanel('budget')
      } else if (isPerformanceDetailPanelOpen) {
        setEditSourcePanel('performance')
      } else {
        setEditSourcePanel('task')
      }

      setIsTaskEditPanelOpen(true)
      // 상세 패널은 열린 상태로 유지 - setIsDetailPanelOpen(false) 제거
    }
  }

  const handleTaskDelete = (task: TaskTreeNode) => {
    console.warn('Task delete requested:', task)
    // TODO: Implement delete functionality
  }

  // Handle task edit panel close
  const handleTaskEditPanelClose = () => {
    console.log('[DEBUG] handleTaskEditPanelClose called', { editSourcePanel })
    setIsTaskEditPanelOpen(false)

    // Only clear edit data for budget/performance contexts
    // Task list context needs editTaskData for the callback
    if (editSourcePanel === 'budget' || editSourcePanel === 'performance') {
      console.log('[DEBUG] Clearing editTaskData in close (budget/performance)')
      setEditTaskData(null)
      setEditSourcePanel(null)
    } else {
      console.log('[DEBUG] NOT clearing editTaskData in close (will be cleared in callback)')
    }
    // 상세 패널은 열린 상태 유지 - selectedTask 제거하지 않음
  }

  // Handle task edit success - context-aware refresh
  const handleTaskEditSuccess = async () => {
    // 세부실행과제 수정 성공 후: 상세 패널은 열린 상태 유지, 데이터만 새로고침
    const editedTaskId = editTaskData?.id

    try {
      if (editSourcePanel === 'budget') {
        // 예산 목록 — 테이블이 트리거를 보고 재조회
        setTaskBudgetRefreshTrigger(prev => prev + 1)

        // 패널이 자체 fetch로 최신 데이터를 다시 가져오도록 refreshKey 증가
        if (editedTaskId) {
          setTaskDetailRefreshKey(k => k + 1)
        }

      } else if (editSourcePanel === 'performance') {
        // 성과 목록 — 테이블이 트리거를 보고 재조회
        setTaskPerformanceRefreshTrigger(prev => prev + 1)

        // 패널이 자체 fetch
        if (editedTaskId) {
          setTaskDetailRefreshKey(k => k + 1)
        }

      } else {
        // Task list context - trigger refresh and update detail panel
        console.log('[DEBUG] Task list context - setting refresh state', {
          editedTaskId,
          editSourcePanel
        })
        setIsRefreshingTask(true)
        setTaskRefreshTrigger(prev => prev + 1)

        // Wait for TasksTable to call onDataRefreshed with updated data
        // The update will happen via handleTasksDataRefreshed callback
        // Note: editTaskData and editSourcePanel will be cleared in the callback
        // after detail panel is updated, NOT in finally block
      }
    } catch (error) {
      console.error('Failed to refresh after task edit:', error)
      // Redux에서 이미 에러 처리됨, 로그만 남김
    } finally {
      console.log('[DEBUG] handleTaskEditSuccess finally block', {
        editSourcePanel,
        willClearEditData: editSourcePanel === 'budget' || editSourcePanel === 'performance'
      })

      // Close edit panel
      setIsTaskEditPanelOpen(false)

      // Only clear edit data for budget/performance contexts
      // Task list context clears these in handleTasksDataRefreshed callback
      if (editSourcePanel === 'budget' || editSourcePanel === 'performance') {
        console.log('[DEBUG] Clearing editTaskData in finally (budget/performance context)')
        setEditTaskData(null)
        setEditSourcePanel(null)
      } else {
        console.log('[DEBUG] NOT clearing editTaskData in finally (task list context)')
      }
      // Note: For task list context, isRefreshingTask and editTaskData are cleared in handleTasksDataRefreshed callback
    }
  }

  // Handle tasks data refreshed from TasksTable
  const handleTasksDataRefreshed = useCallback((tasks: any[]) => {
    console.log('[DEBUG] handleTasksDataRefreshed called', {
      tasksCount: tasks.length,
      isRefreshingTask,
      editTaskDataId: editTaskData?.id,
      condition: isRefreshingTask && editTaskData?.id
    })

    // Only process if we're refreshing after edit
    if (isRefreshingTask && editTaskData?.id) {
      console.log('[DEBUG] Processing task refresh for edit')

      // Find and update the edited task in detail panel
      const updatedTask = tasks.find((t: any) => t.id === editTaskData.id)
      console.log('[DEBUG] Updated task found:', !!updatedTask)

      if (updatedTask) {
        // 패널이 자체 fetch
        setTaskDetailRefreshKey(k => k + 1)
        console.log('[DEBUG] Detail panel refreshKey bumped')
      }

      // Clear refresh state and edit data
      setIsRefreshingTask(false)
      setEditTaskData(null)
      setEditSourcePanel(null)
      console.log('[DEBUG] Refresh state cleared')
    } else {
      console.log('[DEBUG] Skipping task refresh - condition not met')
    }
  }, [isRefreshingTask, editTaskData])

  // Handle task item edit
  const handleTaskItemEdit = (taskItem: TaskTreeNode) => {
    if (taskItem.type === 'task_item' && taskItem.originalData) {
      const taskItemData = taskItem.originalData as any
      setEditTaskItemData({
        id: taskItem.id,
        name: taskItem.name,
        taskId: taskItemData.task_id || '',
        taskName: taskItemData.task_name || '',
        taskSubgroupName: taskItemData.task_subgroup_name || '',
        businessName: taskItem.business_name || '',
        output: taskItemData.output || '',
        startAt: taskItemData.start_at || '',
        endAt: taskItemData.end_at || ''
      })
      setIsTaskItemEditPanelOpen(true)
      // 상세 패널은 열린 상태로 유지 - setIsTaskItemDetailPanelOpen(false) 제거
    }
  }

  // Handle task item edit panel close
  const handleTaskItemEditPanelClose = () => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
    // 상세 패널은 열린 상태 유지 - selectedTaskItem 제거하지 않음
  }

  // (convertTaskItemToTreeNode 제거 — TaskDetailPanel이 자체 fetch + 변환)

  // Handle task item edit success
  const handleTaskItemEditSuccess = async () => {
    // 수행업무 수정 성공 후: 상세 패널은 열린 상태 유지, 데이터만 새로고침
    const editedTaskItemId = editTaskItemData?.id

    // 목록 재조회는 테이블이 트리거를 보고 스스로 수행한다
    setTaskItemRefreshTrigger(prev => prev + 1)

    // 패널이 자체 fetch로 최신 데이터를 다시 가져오도록 refreshKey 증가
    if (editedTaskItemId) {
      setTaskDetailRefreshKey(k => k + 1)
    }
    // 편집 패널만 닫기 (상세 패널은 열린 상태 유지)
  }

  // Handle task item click from TaskItemsTable
  const handleTaskItemClick = (taskItem: TaskTreeNode) => {
    setSelectedTaskItemId(taskItem.id)
    setIsTaskItemDetailPanelOpen(true)
  }

  // Handle task item detail panel close
  const handleTaskItemDetailPanelClose = () => {
    setIsTaskItemDetailPanelOpen(false)
    setSelectedTaskItemId(null)
  }

  // Handle task item approval/cancel success - refresh task items data
  const handleTaskItemApprovalSuccess = useCallback(() => {
    // Refresh both task item list data and summary data after approval/cancel actions
    if (selectedWorkspace?.id && currentUser?.id) {
      // Reload task summary for 기간별 tab
      if (periodViewType === 'period') {
        dispatch(fetchTaskSummary({
          workspaceId: selectedWorkspace.id,
          stakeholderId: currentUser.id,
          departmentId: selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : null,
          businessId: selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : null,
          startDate: startDate ? formatDateForAPI(startDate) : null,
          endDate: endDate ? formatDateForAPI(endDate) : null
        }))
      }

      // Reload monthly summary for 전체 tab
      if (periodViewType === 'all') {
        fetchMonthlySummary()
      }

      // 결재는 필터를 바꾸지 않으므로 트리거를 올려 목록을 다시 조회한다
      setTaskItemRefreshTrigger(prev => prev + 1)
    }
  }, [
    selectedWorkspace?.id,
    currentUser?.id,
    periodViewType,
    dispatch,
    selectedDepartmentId,
    selectedDepartmentName,
    selectedBusinessId,
    selectedBusinessName,
    searchQuery,
    startDate,
    endDate,
    fetchMonthlySummary
  ])

  // Handle budget item click — 패널이 자체 fetch
  const handleBudgetItemClick = (taskId: string) => {
    if (!taskId) return
    setSelectedBudgetTaskId(taskId)
    setIsBudgetDetailPanelOpen(true)
  }

  // Handle budget detail panel close
  const handleBudgetDetailPanelClose = () => {
    setIsBudgetDetailPanelOpen(false)
    setSelectedBudgetTaskId(null)
  }

  // Handle performance item click — 패널이 자체 fetch
  const handlePerformanceItemClick = (taskId: string) => {
    if (!taskId) return
    setSelectedPerformanceTaskId(taskId)
    setIsPerformanceDetailPanelOpen(true)
  }

  // Handle performance detail panel close
  const handlePerformanceDetailPanelClose = () => {
    setIsPerformanceDetailPanelOpen(false)
    setSelectedPerformanceTaskId(null)
  }

  // Handle report button click from TasksTable
  const handleReportClick = async (task: TaskTreeNode) => {
    // Set action button click flag to prevent row click
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    // Close any open detail panels to prevent double panels
    setIsDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)

    // Fetch report templates with business_id from the selected task
    if (selectedWorkspace?.id) {
      const businessId = task.originalData?.business_id || task.business_id
      const templates = await fetchReportTemplates(selectedWorkspace.id, businessId)

      // Try to fetch existing report data if templates are available
      if (templates && templates.length > 0) {
        const firstTemplate = templates[0]

        try {
          // Fetch existing report detail for the first template
          const reportDetail = await TaskReportService.appsTaskViewsGetTaskReportDetail({
            taskId: task.id,
            reportTemplateId: firstTemplate.key
          })

          // 패널이 자체 fetch하므로 fetchedReport 보관 불필요
          setReportPanelState({
            isOpen: true,
            selectedTask: task,
            selectedTemplate: { key: firstTemplate.key, name: firstTemplate.name },
          })
          void reportDetail
        } catch (error: any) {
          console.log('No existing report found or error fetching report:', error)
          setReportPanelState({
            isOpen: true,
            selectedTask: task,
            selectedTemplate: { key: firstTemplate.key, name: firstTemplate.name },
          })
        }
      } else {
        setReportPanelState({
          isOpen: true,
          selectedTask: task,
          selectedTemplate: null,
        })
      }
    } else {
      setReportPanelState({
        isOpen: true,
        selectedTask: task,
        selectedTemplate: null,
      })
    }
  }

  // Handle report detail panel close
  const handleReportDetailPanelClose = () => {
    setReportPanelState({
      isOpen: false,
      selectedTask: null,
      selectedTemplate: null,
    })
  }

  // (convertTaskTreeNodeToReportResponse 제거 — ReportDetailPanel이 자체 fetch)

  // Fetch report templates from API
  const fetchReportTemplates = useCallback(async (workspaceId: string, businessId?: string | null) => {
    if (!workspaceId) return null

    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)

    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId,
        businessId: businessId || undefined
      })
      setReportTemplates(response || [])
      return response || []  // Return templates for immediate use
    } catch (error: any) {
      console.error('Failed to fetch report templates:', error)
      setReportTemplatesError('보고서 템플릿 목록을 불러오는데 실패했습니다.')
      return null
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }, [])

  // Handle report template change — 패널이 reportTemplateKey prop 변경을 감지해 자체 refetch
  const handleReportTemplateChange = (templateKey: string, templateName: string) => {
    setReportPanelState(prev => ({
      ...prev,
      selectedTemplate: templateKey ? { key: templateKey, name: templateName } : null,
    }))
  }

  // Handle report update — 저장 후 알림용. 패널이 자체 refetch하므로 추가 작업 없음.
  const handleReportUpdate = useCallback(() => {
    // 필요시 다른 목록 새로고침을 여기 추가 가능
  }, [])

  // Handle task edit from report panel
  const handleReportTaskEdit = useCallback((taskId: string) => {
    const selectedTask = reportPanelState.selectedTask
    if (!selectedTask) return

    // Close report panel first using flushSync to ensure state is updated immediately
    flushSync(() => {
      handleReportDetailPanelClose()
    })
    // Then open edit panel
    handleTaskEdit(selectedTask)
  }, [reportPanelState.selectedTask])

  // Handle task detail view from report panel
  const handleReportTaskDetail = useCallback((taskId: string) => {
    const selectedTask = reportPanelState.selectedTask
    if (!selectedTask) return

    // Close report panel first using flushSync to ensure state is updated immediately
    flushSync(() => {
      handleReportDetailPanelClose()
    })
    // Then open detail panel
    handleTaskRowClick(selectedTask)
  }, [reportPanelState.selectedTask])

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = (newStartDate: Date | null, newEndDate: Date | null) => {
    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  // Get title text based on selected list type and period view (personalized for current user)
  const getListTitle = () => {
    switch (selectedListType) {
      case 'task':
        return periodViewType === 'all' ? '내 세부실행과제 월별 진행율' : '내 세부실행과제 목록'
      case 'work':
        return periodViewType === 'all' ? '내 수행업무 월별 진행율' : '내 수행업무 목록'
      case 'budget':
        return periodViewType === 'all' ? '내 예산 월별 집행률' : '내 예산 목록'
      case 'performance':
        return periodViewType === 'all' ? '내 성과 월별 달성률' : '내 성과 목록'
      default:
        return '내 세부실행과제 목록'
    }
  }

  // Helper functions for formatting
  const formatNumber = (value: number | null, unit?: string) => {
    if (value === null) {
      return '-'
    }
    if (unit === '원') {
      return `${new Intl.NumberFormat('ko-KR').format(value)}${unit}`
    }
    return `${new Intl.NumberFormat('ko-KR').format(value)}개`
  }

  // Progress Bar Cell Renderer - Based on Figma Design
  const ProgressBarRenderer = (params: any) => {
    const ratio = params.value
    if (typeof ratio !== 'number') return '-'

    return (
      <div className="flex items-center gap-3 h-full w-full">
        {/* Progress Bar - Figma Design: gray background with primary fill */}
        <div
          className="flex-1"
          style={{
            height: '22px',
            backgroundColor: '#D0D0D0', // Figma: text/gray-2 color
            borderRadius: '6px', // Figma: 6px border radius
            position: 'relative',
            overflow: 'hidden',
            minWidth: '200px', // 최소 너비 보장
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${ratio}%`,
              backgroundColor: '#2F4062', // Figma: color/primary
              borderRadius: '6px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>

      </div>
    )
  }

  // Item Name Cell Renderer
  const ItemNameRenderer = (params: any) => {
    return (
      <div className="flex items-center gap-2 h-full">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: designTokens.colors.gray[4] }}
        />
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.gray[6],
          }}
        >
          {params.value}
        </span>
      </div>
    )
  }

  // Column definitions for Summary DataGrid
  const summaryColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '항목명',
      field: 'itemName',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellRenderer: ItemNameRenderer,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
    },
    {
      headerName: '전체',
      field: 'totalFormatted',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px',
        cursor: 'pointer',
        textDecoration: 'underline',
        color: designTokens.colors.primary.DEFAULT,
      }
    },
    {
      headerName: '완료',
      field: 'completedFormatted',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px',
        cursor: 'pointer',
        textDecoration: 'underline',
        color: designTokens.colors.primary.DEFAULT,
      }
    },
    {
      headerName: '미완료',
      field: 'incompleteFormatted',
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellStyle: {
        textAlign: 'right',
        padding: '0 16px',
        cursor: 'pointer',
        textDecoration: 'underline',
        color: designTokens.colors.primary.DEFAULT,
      }
    },
    {
      headerName: '비율',
      field: 'ratio',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      filter: false,
      valueFormatter: (params) => params.value === null ? '-' : `${params.value}%`,
      suppressSizeToFit: true,
    },
    {
      headerName: '그래프',
      field: 'ratio',
      colId: 'graph',
      flex: 1, // 남는 공간을 모두 차지
      minWidth: 280,
      cellRenderer: ProgressBarRenderer,
      sortable: false,
      filter: false,
      suppressSizeToFit: false, // flex 사용 시 필요
    },
  ], [])

  // Prepare data for DataGrid with incomplete formatting
  const gridData = useMemo(() =>
    summaryData.map(item => ({
      ...item,
      totalFormatted: formatNumber(item.total, item.unit),
      completedFormatted: formatNumber(item.completed, item.unit),
      incompleteFormatted: formatNumber(item.incomplete, item.unit),
    })), [summaryData],
  )

  // 요약표의 전체/완료/미완료 셀 클릭 → 해당 리스트 사이드패널 열기
  const handleSummaryCellClicked = useCallback((event: any) => {
    const itemName = event.data?.itemName as SummaryDataItem['itemName'] | undefined
    const colId = event.column?.getColId?.() as string | undefined
    if (!itemName || !colId) return

    // 클릭 가능한 컬럼만 처리
    const bucketByCol: Record<string, SummaryListPanelStatus | undefined> = {
      totalFormatted: 'all',
      completedFormatted: 'completed',
      incompleteFormatted: 'incomplete',
    }
    const statusBucket = bucketByCol[colId]
    if (!statusBucket) return

    const typeByName: Record<SummaryDataItem['itemName'], SummaryListPanelType> = {
      '수행업무': 'task_item',
      '세부실행과제': 'task',
      '예산': 'budget',
      '성과': 'performance',
    }
    const type = typeByName[itemName]

    const bucketLabel = statusBucket === 'all' ? '전체' : statusBucket === 'completed' ? '완료' : '미완료'
    setSummaryListPanel({
      type,
      statusBucket,
      title: `내 ${itemName} 목록 (${bucketLabel})`,
    })
  }, [])

  // statusBucket → 테이블에 전달할 status 필터로 매핑.
  // - 수행업무: 4개 세부 상태(incomplete/assigned/completed/reviewed) 중 대표값으로 단일 필터.
  // - 세부실행과제: 2개 상태(incomplete/completed)와 1:1 대응.
  // - 예산/성과: status 필터 미지원 → 항상 undefined (제목으로 컨텍스트만 전달)
  const resolveStatusFilter = useCallback(
    (type: SummaryListPanelType, bucket: SummaryListPanelStatus): string | undefined => {
      if (bucket === 'all') return undefined
      if (type === 'task_item') return bucket === 'completed' ? 'completed' : 'incomplete'
      if (type === 'task') return bucket === 'completed' ? 'completed' : 'incomplete'
      return undefined
    },
    [],
  )

  const closeSummaryListPanel = useCallback(() => setSummaryListPanel(null), [])

  // ApexCharts는 옵션 객체가 새로 생성돼도 이벤트 핸들러를 항상 재바인딩하지 않아
  // 초기 렌더 시점의 stale closure를 들고 있을 수 있다. 이를 우회하기 위해 각 차트별
  // "최신 click 핸들러"를 ref로 보관하고, ApexCharts 이벤트는 항상 ref.current를 호출만 한다.
  // ref.current는 각 차트의 렌더 IIFE에서 매번 최신 monthKeys/closure로 갱신된다.
  const taskChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)
  const workChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)
  const budgetChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)
  const performanceChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)

  // chartType → 패널 타입/이름 매핑 (컨테이너 클릭/포인트 클릭 공용)
  const chartTypeToPanelType = useMemo(() => ({
    work: 'task_item',
    task: 'task',
    budget: 'budget',
    performance: 'performance',
  } as const), [])

  const itemNameByType: Record<SummaryListPanelType, string> = useMemo(() => ({
    task_item: '수행업무',
    task: '세부실행과제',
    budget: '예산',
    performance: '성과',
  }), [])

  // 차트의 특정 봉/포인트 또는 x축 월 라벨 클릭 → 해당 월(monthKey: YYYY-MM)로 기간 좁힘.
  const handleChartPointClick = useCallback((chartType: 'task' | 'work' | 'budget' | 'performance', monthKey: string | undefined) => {
    const type = chartTypeToPanelType[chartType]
    if (!type) return
    if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) {
      // monthKey가 유효하지 않으면 전체로 폴백
      setSummaryListPanel({
        type,
        statusBucket: 'all',
        title: `내 ${itemNameByType[type]} 목록 (전체)`,
      })
      return
    }
    const [yearStr, monthStr] = monthKey.split('-')
    const year = parseInt(yearStr, 10)
    const monthIdx = parseInt(monthStr, 10) - 1 // 0-based
    const start = new Date(year, monthIdx, 1)
    // 다음 달 1일 00:00의 직전(말일 23:59:59.999)
    const end = new Date(year, monthIdx + 1, 1)
    end.setMilliseconds(end.getMilliseconds() - 1)
    setSummaryListPanel({
      type,
      statusBucket: 'all',
      title: `내 ${itemNameByType[type]} 목록 (${year}년 ${monthIdx + 1}월)`,
      dateRangeOverride: { start, end },
    })
  }, [chartTypeToPanelType, itemNameByType])

  // ApexCharts click 이벤트 → 핸들러 빌더.
  // (idx, monthKeys)를 받아 클릭한 데이터 포인트가 있을 때만 해당 월로 좁힌 패널을 연다.
  const buildChartClickHandler = useCallback((
    chartType: 'task' | 'work' | 'budget' | 'performance',
    monthKeys: string[],
  ) => {
    return (idx: number) => {
      // 월별 봉/포인트 또는 x축 월 라벨을 정확히 클릭했을 때만 해당 월 목록을 연다.
      // 그 외 차트 빈 영역 클릭은 무시한다 — 기간 필터 없는 목록이 열리면 사용자가 오류로 인지한다.
      if (idx >= 0 && monthKeys[idx]) {
        handleChartPointClick(chartType, monthKeys[idx])
      }
    }
  }, [handleChartPointClick])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0],
        }}
      >
        {/* Date Range Picker - Conditional */}
        {periodViewType === 'period' && (
          <div className="flex-shrink-0">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={handleDateRangeChange}
              timePeriod={selectedTimePeriod}
              onTimePeriodChange={handleTimePeriodChange}
              triggerMode="icon"
              showDateText={true}
              workspaceId={selectedWorkspace?.id}
            />
          </div>
        )}

        {/* Period Type Toggle */}
        <div className="inline-flex rounded-md shadow-sm h-10 flex-shrink-0" role="group">
          <button
            type="button"
            onClick={() => handlePeriodViewChange('period')}
            className="px-4 font-medium border rounded-l-md transition-colors h-10 whitespace-nowrap"
            style={{
              backgroundColor: periodViewType === 'period' ? designTokens.colors.primary.DEFAULT : designTokens.colors.white,
              color: periodViewType === 'period' ? designTokens.colors.white : designTokens.colors.gray[6],
              borderColor: designTokens.colors.gray[2],
              fontFamily: designTokens.typography.fontFamily.sans[0],
            }}
          >
            기간별
          </button>
          <button
            type="button"
            onClick={() => handlePeriodViewChange('all')}
            className="px-4 font-medium border rounded-r-md transition-colors h-10 whitespace-nowrap"
            style={{
              backgroundColor: periodViewType === 'all' ? designTokens.colors.primary.DEFAULT : designTokens.colors.white,
              color: periodViewType === 'all' ? designTokens.colors.white : designTokens.colors.gray[6],
              borderColor: designTokens.colors.gray[2],
              borderLeft: 'none',
              fontFamily: designTokens.typography.fontFamily.sans[0],
            }}
          >
            전체
          </button>
        </div>

        {/* Business Selection */}
        <div className="w-[200px] flex-shrink-0">
          <BusinessSelect
            workspaceId={selectedWorkspace?.id || ''}
            businesses={businesses}
            isLoading={businessLoading}
            businessesError={businessError}
            selectedBusinessId={selectedBusinessId}
            selectedBusinessName={selectedBusinessName || '[전체] 사업 선택'}
            onSelect={handleBusinessSelect}
            placeholder="[전체] 사업 선택"
            showAllOption={true}
          />
        </div>

        {/* Department Selection */}
        <div className="w-[200px] flex-shrink-0">
          <DepartmentSelect
            workspaceId={selectedWorkspace?.id || ''}
            selectedDepartmentId={selectedDepartmentId}
            selectedDepartmentName={selectedDepartmentName || '[전체] 부서 선택'}
            onSelect={handleDepartmentSelect}
            placeholder="[전체] 부서 선택"
            showAllOption={true}
          />
        </div>

        {/* Clear Filters Button - conditional */}
        {((selectedDepartmentId && selectedDepartmentName !== '전체') ||
          (selectedBusinessId && selectedBusinessName !== '전체')) && (
          <Button
            size="medium"
            color="white"
            onClick={clearAllFilters}
            className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
          >
            필터 초기화
          </Button>
        )}
      </div>

      {/* Summary Title Section */}
      <div
        className="flex items-center gap-[10px] py-[18px] flex-shrink-0"
      >
        <div className="flex items-center justify-center gap-[18px]">
          <div className="flex items-center gap-[10px]">
            <h2
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-small'][0],
                fontWeight: 700,
                lineHeight: designTokens.typography.fontSize['title-small'][1].lineHeight,
                color: designTokens.colors.gray[6],
              }}
            >
              요약
              {((periodViewType === 'all' && monthlySummaryError) || (periodViewType === 'period' && summaryError)) && (
                <span style={{
                  fontSize: '14px',
                  color: '#dc2626',
                  marginLeft: '16px'
                }}>
                  - 데이터 로드 실패
                </span>
              )}
              {((periodViewType === 'all' && isMonthlySummaryLoading) || (periodViewType === 'period' && isSummaryLoading)) && (
                <span style={{
                  fontSize: '14px',
                  color: designTokens.colors.gray[4],
                  marginLeft: '16px'
                }}>
                  - 로딩 중...
                </span>
              )}
            </h2>
          </div>
        </div>
      </div>

      {/* Summary Table - DataGrid */}
      <div className="flex-shrink-0">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          <div className="w-full"> {/* Full width container for responsive layout */}
            <DataGrid
              rowData={gridData}
              columnDefs={summaryColumnDefs}
              headerHeight={40}
              rowHeight={42}
              height={40 + (gridData.length * 42) + 2} // header + (rows * rowHeight) + border
              rowSelection="single"
              animateRows={true}
              onCellClicked={handleSummaryCellClicked}
              getRowStyle={(params) => {
                if (params.node.rowIndex! % 2 === 0) {
                  return { backgroundColor: designTokens.colors.gray[0] }
                }
                return { backgroundColor: designTokens.colors.white }
              }}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: false,
                suppressSizeToFit: true,
              }}
              // 범위 선택 및 복사 기능 활성화
              enableRangeSelection={true}
              rangeSelectionExcludeColumns={['graph']}
              enableCellCopy={true}
              suppressRowClickSelection={true}
              rangeCopyFormatter={(colId, value) => {
                // 비율 포맷팅
                if (colId === 'ratio') {
                  return value !== null && value !== undefined ? `${value}%` : '-'
                }
                // 기본값 처리
                if (value === null || value === undefined) return '-'
                return value.toString()
              }}
            />
          </div>
        </div>
      </div>

      {/* List Title Section - Updated for personal context */}
      <div className="mt-6 flex-shrink-0">
        <div
          className="flex items-center justify-between gap-[10px] py-[10px]"
          style={{
            borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
          }}
        >
          {/* Left: List Title */}
          <div className="flex items-center gap-[18px]">
            <div className="flex items-center gap-[10px]">
              <h2
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '20px', // title/small from Figma
                  lineHeight: '1.193359375em',
                  fontWeight: 700,
                  color: '#1B1B1B', // text/gray-6 from Figma
                }}
              >
                {getListTitle()}
              </h2>
            </div>
          </div>

          {/* Right: Status Filter + Search + Tab Menu */}
          <div className="flex items-center gap-3">
            {/* Status Filter (only for work tab) */}
            {selectedListType === 'work' && (
              <div className="relative">
                <select
                  value={taskItemStatusFilter}
                  onChange={(e) => setTaskItemStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">[전체] 수행업무 상태</option>
                  <option value="incomplete">미완료</option>
                  <option value="assigned">할당됨</option>
                  <option value="completed">완료</option>
                  <option value="reviewed">검토완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Status Filter (only for task tab) */}
            {selectedListType === 'task' && (
              <div className="relative">
                <select
                  value={taskStatusFilter}
                  onChange={(e) => setTaskStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">[전체] 세부실행과제 상태</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Status Filter (only for budget tab) */}
            {selectedListType === 'budget' && (
              <div className="relative">
                <select
                  value={budgetStatusFilter}
                  onChange={(e) => setBudgetStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">[전체] 예산 상태</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Status Filter (only for performance tab) */}
            {selectedListType === 'performance' && (
              <div className="relative">
                <select
                  value={performanceStatusFilter}
                  onChange={(e) => setPerformanceStatusFilter(e.target.value)}
                  className="h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[6],
                    minWidth: '120px'
                  }}
                >
                  <option value="">[전체] 성과 상태</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: designTokens.colors.gray[4] }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Search Input and Button */}
            <div className="flex items-center gap-2">
              {/* Search Input */}
              <div className="relative w-[300px]">
                <div
                  className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
                  style={{
                    backgroundColor: designTokens.colors.white,
                    borderColor: designTokens.colors.gray[1]
                  }}
                >
                  <Search
                    className="w-5 h-5"
                    style={{ color: designTokens.colors.gray[3] }}
                  />
                  <input
                    type="text"
                    placeholder="명칭으로 검색해주세요."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="flex-1 bg-transparent outline-none"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                      color: searchInput ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch()
                      }
                    }}
                  />
                </div>
              </div>

              {/* Search Button */}
              <Button
                color="dark"
                size="medium"
                onClick={handleSearch}
                className="h-10 px-[10px]"
              >
                검색
              </Button>
            </div>

            {/* Tab Menu */}
            <div
              className="flex"
              style={{
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
            {/* 수행업무 Tab */}
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'work' ? '#2F4062' : '#EBEBEB',
                borderRight: '1px solid #BDBDBD',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 600,
                color: selectedListType === 'work' ? '#EBEBEB' : '#707071',
              }}
              onClick={() => handleListTypeChange('work')}
            >
              수행업무
            </div>
            {/* 과제 Tab */}
            {showTasks && (
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'task' ? '#2F4062' : '#EBEBEB', // color/primary or text/gray-1
                borderRight: '1px solid #BDBDBD', // text/gray-3
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px', // body/large-bold from Figma
                fontWeight: 600,
                color: selectedListType === 'task' ? '#EBEBEB' : '#707071', // text/gray-1 or text/gray-4
              }}
              onClick={() => handleListTypeChange('task')}
            >
              세부실행과제
            </div>
            )}

            {/* 예산 Tab */}
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'budget' ? '#2F4062' : '#EBEBEB',
                borderRight: '1px solid #BDBDBD',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 600,
                color: selectedListType === 'budget' ? '#EBEBEB' : '#707071',
              }}
              onClick={() => handleListTypeChange('budget')}
            >
              예산
            </div>

            {/* 성과 Tab */}
            <div
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{
                width: '120px',
                height: '40px',
                backgroundColor: selectedListType === 'performance' ? '#2F4062' : '#EBEBEB',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 600,
                color: selectedListType === 'performance' ? '#EBEBEB' : '#707071',
              }}
              onClick={() => handleListTypeChange('performance')}
            >
              성과
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Monthly Task Chart - 전체 탭에서 과제 탭이 선택되었을 때만 표시. 월별 봉/x축 월 라벨 클릭 시 해당 월 목록 오픈 */}
      {selectedListType === 'task' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyDataToChart(monthlySummary.task.monthly)
                // 매 렌더마다 최신 monthKeys로 ref 갱신
                taskChartHandlerRef.current = buildChartClickHandler('task', chartData.monthKeys)
                const chartOptions = getChartOptions(
                  chartData.categories,
                  'task',
                  chartData.series,
                  taskChartHandlerRef,
                )

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Task Item Chart */}
      {selectedListType === 'work' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyTaskItemDataToChart(monthlySummary.task_item.monthly)
                workChartHandlerRef.current = buildChartClickHandler('work', chartData.monthKeys)
                const chartOptions = getChartOptions(
                  chartData.categories,
                  'work',
                  chartData.series,
                  workChartHandlerRef,
                )

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Task Budget Chart */}
      {selectedListType === 'budget' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyTaskBudgetDataToChart(monthlySummary.task_budget.monthly)
                budgetChartHandlerRef.current = buildChartClickHandler('budget', chartData.monthKeys)
                const chartOptions = getChartOptions(
                  chartData.categories,
                  'budget',
                  chartData.series,
                  budgetChartHandlerRef,
                )

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Task Performance Chart */}
      {selectedListType === 'performance' && periodViewType === 'all' && monthlySummary && (
        <div className="mt-6 flex-shrink-0">
          <div
            className="bg-white rounded-lg shadow-sm border"
            style={{ borderColor: designTokens.colors.gray[1] }}
            title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월 목록을 사이드패널로 봅니다"
          >
            <div className="p-6">
              {(() => {
                const chartData = transformMonthlyTaskPerformanceDataToChart(monthlySummary.task_performance.monthly)
                performanceChartHandlerRef.current = buildChartClickHandler('performance', chartData.monthKeys)
                const chartOptions = getChartOptions(
                  chartData.categories,
                  'performance',
                  chartData.series,
                  performanceChartHandlerRef,
                )

                return (
                  <Chart
                    type="line"
                    series={chartData.series}
                    options={chartOptions}
                    height={400}
                  />
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Task List DataGrid - 기간별 탭에서 과제 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'task' && periodViewType === 'period' && (
        <div className="mt-6 flex-shrink-0">
          <TasksTable
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId && selectedBusinessName !== '전체' ? selectedBusinessId : undefined}
            selectedDepartmentId={selectedDepartmentId && selectedDepartmentName !== '전체' ? selectedDepartmentId : undefined}
            selectedMemberId={currentUser?.id || ''} // Always use current user (org member id)
            isVisible={true}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            onTaskClick={handleTaskRowClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
            refreshTrigger={taskRefreshTrigger}
            onDataRefreshed={handleTasksDataRefreshed}
            status={taskStatusFilter || undefined}
          />
        </div>
      )}

      {/* 업무 목록 테이블 - 기간별 탭에서 업무 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'work' && periodViewType === 'period' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskItemsTable
            refreshTrigger={taskItemRefreshTrigger}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={currentUser?.id || ''} // Always use current user (org member id)
            isVisible={true}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            onTaskItemClick={handleTaskItemClick}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            status={taskItemStatusFilter || undefined}
          />
        </div>
      )}

      {/* 예산 목록 테이블 - 기간별 탭에서 예산 탭이 선택되었을 때만 표시 */}
      {selectedListType === 'budget' && periodViewType === 'period' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskBudgetsTable
            refreshTrigger={taskBudgetRefreshTrigger}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={currentUser?.id || ''} // Always use current user (org member id)
            isVisible={true}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={budgetStatusFilter || undefined}
            onBudgetItemClick={handleBudgetItemClick}
          />
        </div>
      )}

      {/* 성과 목록 테이블 - 기간별 탭에서만 표시 */}
      {selectedListType === 'performance' && periodViewType === 'period' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskPerformancesTable
            refreshTrigger={taskPerformanceRefreshTrigger}
            workspaceId={selectedWorkspace?.id || ''}
            selectedBusinessId={selectedBusinessId}
            selectedDepartmentId={selectedDepartmentId}
            selectedMemberId={currentUser?.id || ''} // Always use current user (org member id)
            isVisible={true}
            startDate={periodViewType === 'period' ? startDate : null}
            endDate={periodViewType === 'period' ? endDate : null}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={performanceStatusFilter || undefined}
            onPerformanceItemClick={handlePerformanceItemClick}
          />
        </div>
      )}

      {/* 다른 탭들은 나중에 구현 */}
      {selectedListType !== 'task' && selectedListType !== 'work' && selectedListType !== 'budget' && selectedListType !== 'performance' && (
        <div className="mt-6 flex-1 min-h-0">
          <div
            className="flex items-center justify-center h-32 text-center"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[4],
            }}
          >
            {getListTitle()} 기능은 준비 중입니다.
          </div>
        </div>
      )}

      {/* Task Detail Panel — id만 전달, 패널이 자체 fetch */}
      <TaskDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleDetailPanelClose}
        taskId={selectedTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
        onDelete={handleTaskDelete}
        onSuccess={undefined}
      />

      {/* Task Item Detail Panel */}
      <TaskDetailPanel
        isOpen={isTaskItemDetailPanelOpen}
        onClose={handleTaskItemDetailPanelClose}
        taskId={selectedTaskItemId}
        taskType="task_item"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskItemEdit}
        onDelete={undefined}
        onSuccess={handleTaskItemApprovalSuccess}
      />

      {/* Budget Task Detail Panel */}
      <TaskDetailPanel
        isOpen={isBudgetDetailPanelOpen}
        onClose={handleBudgetDetailPanelClose}
        taskId={selectedBudgetTaskId}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
        onDelete={undefined}
        onSuccess={undefined}
      />

      {/* Performance Task Detail Panel */}
      <TaskDetailPanel
        isOpen={isPerformanceDetailPanelOpen}
        onClose={handlePerformanceDetailPanelClose}
        taskId={selectedPerformanceTaskId}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
        onDelete={undefined}
        onSuccess={undefined}
      />

      {/* Task Edit Panel — budget/performance 초기값은 editTaskData.taskOriginal에서 추출 */}
      {editTaskData && (() => {
        const taskOriginal = editTaskData.taskOriginal

        return (
          <TaskCreatePanel
            isOpen={isTaskEditPanelOpen}
            onClose={handleTaskEditPanelClose}
            onSuccess={handleTaskEditSuccess}
            taskSubgroupId={editTaskData.taskSubgroupId}
            taskSubgroupName={editTaskData.taskSubgroupName}
            taskGroupName={editTaskData.taskGroupName}
            businessName={editTaskData.businessName}
            businessId={editTaskData.businessId}
            editMode={true}
            taskId={editTaskData.id}
            initialTaskData={{
              name: editTaskData.name,
              assigneeId: taskOriginal?.assignee?.id || '',
              assigneeName: taskOriginal?.assignee?.name || '',
              subAssigneeId: taskOriginal?.sub_assignee?.id || '',
              subAssigneeName: taskOriginal?.sub_assignee?.name || '',
              managerId: taskOriginal?.manager?.id || '',
              managerName: taskOriginal?.manager?.name || '',
              reviewerId: taskOriginal?.reviewer?.id || '',
              reviewerName: taskOriginal?.reviewer?.name || '',
              departmentId: taskOriginal?.department_id || '',
              departmentName: taskOriginal?.department_name || '',
              startAt: editTaskData.startAt,
              endAt: editTaskData.endAt,
              description: editTaskData.description,
              budget: taskOriginal?.budget ? {
                id: taskOriginal.budget.id || '',
                resultDescription: taskOriginal.budget.result_description || '',
                items: (taskOriginal.budget.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              performance: taskOriginal?.performance ? {
                id: taskOriginal.performance.id || '',
                name: taskOriginal.performance.name || '',
                unit: taskOriginal.performance.unit || '',
                items: (taskOriginal.performance.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              labels: taskOriginal?.labels || [],
              detailInfos: taskOriginal?.detail_infos || [],
              extraInfos: taskOriginal?.extra_infos || [],
              judgmentBasis: taskOriginal?.judgment_basis || ''
            }}
          />
        )
      })()}

      {/* Task Item Edit Panel */}
      {editTaskItemData && (
        <TaskItemCreatePanel
          isOpen={isTaskItemEditPanelOpen}
          onClose={handleTaskItemEditPanelClose}
          onSuccess={handleTaskItemEditSuccess}
          taskId={editTaskItemData.taskId}
          taskName={editTaskItemData.taskName}
          taskSubgroupName={editTaskItemData.taskSubgroupName}
          businessName={editTaskItemData.businessName}
          editMode={true}
          taskItemId={editTaskItemData.id}
          initialTaskItemData={{
            name: editTaskItemData.name,
            startAt: editTaskItemData.startAt,
            endAt: editTaskItemData.endAt,
            output: editTaskItemData.output
          }}
        />
      )}

      {/* Report Detail Panel — id/templateKey만 전달, 패널이 자체 fetch */}
      <ReportDetailPanel
        isOpen={reportPanelState.isOpen}
        onClose={handleReportDetailPanelClose}
        taskId={reportPanelState.selectedTask?.id ?? null}
        reportTemplateKey={reportPanelState.selectedTemplate?.key ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={selectedWorkspace?.id || ''}
        onReportTypeChange={handleReportTemplateChange}
        onReportUpdate={handleReportUpdate}
        onEditTask={handleReportTaskEdit}
        onTaskClick={handleReportTaskDetail}
      />

      {/* 요약표 셀 / 차트 클릭 → 항목별 리스트 사이드패널 (한 번에 하나만 열림).
          공통 필터(워크스페이스/사업/기간/검색) + 본인이 담당자/관리자/검토자/부담당자인 항목으로 좁힘.
          - 차트 봉/포인트를 정확히 클릭한 경우: 해당 월 범위(dateRangeOverride)로 좁힘
          - 그 외(컨테이너 클릭 / 요약표 셀): 페이지 기간 필터(periodViewType==='period'일 때만 startDate/endDate) 사용
          항목 클릭 시 기존 Detail 패널 재사용. */}
      {(() => {
        const override = summaryListPanel?.dateRangeOverride
        const panelStartDate = override ? override.start : (periodViewType === 'period' ? startDate : null)
        const panelEndDate = override ? override.end : (periodViewType === 'period' ? endDate : null)
        const isBusinessLocked = !!selectedBusinessId && selectedBusinessName !== '전체'
        const commonProps = {
          onClose: closeSummaryListPanel,
          workspaceId: selectedWorkspace?.id || '',
          selectedBusinessId: isBusinessLocked ? selectedBusinessId : undefined,
          selectedBusinessName: isBusinessLocked ? selectedBusinessName : undefined,
          selectedMemberId: currentUser?.id || undefined,
          selectedMemberName: currentUser?.account_name || undefined,
          startDate: panelStartDate,
          endDate: panelEndDate,
          searchQuery: searchQuery || undefined,
        }
        return (
          <>
            <TaskItemsListPanel
              refreshTrigger={taskItemRefreshTrigger}
              {...commonProps}
              isOpen={summaryListPanel?.type === 'task_item'}
              title={summaryListPanel?.type === 'task_item' ? summaryListPanel.title : '수행업무 목록'}
              status={summaryListPanel?.type === 'task_item' ? resolveStatusFilter('task_item', summaryListPanel.statusBucket) : undefined}
              onTaskItemClick={(item) => handleTaskItemClick(item)}
            />
            <TasksListPanel
              {...commonProps}
              isOpen={summaryListPanel?.type === 'task'}
              title={summaryListPanel?.type === 'task' ? summaryListPanel.title : '세부실행과제 목록'}
              status={summaryListPanel?.type === 'task' ? resolveStatusFilter('task', summaryListPanel.statusBucket) : undefined}
              onTaskClick={(task) => handleTaskRowClick(task)}
              onReportClick={handleReportClick}
              isActionButtonClicked={isActionButtonClicked}
            />
            <TaskBudgetsListPanel
              refreshTrigger={taskBudgetRefreshTrigger}
              {...commonProps}
              isOpen={summaryListPanel?.type === 'budget'}
              title={summaryListPanel?.type === 'budget' ? summaryListPanel.title : '예산 목록'}
              onBudgetItemClick={(taskId) => handleBudgetItemClick(taskId)}
            />
            <TaskPerformancesListPanel
              refreshTrigger={taskPerformanceRefreshTrigger}
              {...commonProps}
              isOpen={summaryListPanel?.type === 'performance'}
              title={summaryListPanel?.type === 'performance' ? summaryListPanel.title : '성과 목록'}
              onPerformanceItemClick={(taskId) => handlePerformanceItemClick(taskId)}
            />
          </>
        )
      })()}
    </div>
  )
}

export default MyTaskDashboardPage